import { useState } from 'react';
import SearchInput from '@rossum/components/search-input/SearchInput';
import { IconCirclePlus } from '@rossum/ui/icons/tabler';
import {
  Button,
  CircularProgress,
  MenuItem,
  Popover,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { useIntl } from '../../ComponentsContext';
import { FilterSettings, FiltersObject } from '../types';

const FILTER_ITEMS_MENU_WIDTH = 252;

type AddFilterProps<O extends FiltersObject> = {
  filterSettings: FilterSettings<O>[];
  isLoading?: boolean;
};

export const AddFilter = <O extends FiltersObject>({
  filterSettings,
  isLoading,
}: AddFilterProps<O>) => {
  const [filterCandidate, setFilterCandidate] =
    useState<FilterSettings<O> | null>(null);

  const [searchValue, setSearchValue] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const intl = useIntl();

  const filteredSettings = filterSettings.filter(
    filter =>
      !searchValue ||
      filter.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const onClose = () => {
    setSearchValue('');
    setAnchorEl(null);
  };

  return (
    <>
      <Popover
        open={!!anchorEl}
        onClose={onClose}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorEl={anchorEl}
        slotProps={{
          transition: {
            onExited: () => setFilterCandidate(null),
          },
          paper: {
            sx: {
              width: filterCandidate ? 'auto' : FILTER_ITEMS_MENU_WIDTH,
            },
          },
        }}
      >
        <Stack sx={{ overflow: 'hidden' }}>
          {filterCandidate ? (
            <filterCandidate.Component
              {...filterCandidate}
              onBackClick={() => setFilterCandidate(null)}
              onClose={onClose}
              currentValue={undefined}
              confirmFilter={filterCandidate.setFilter}
            />
          ) : (
            <Stack sx={{ p: 2 }}>
              <SearchInput
                onChange={setSearchValue}
                value={searchValue}
                withStartAdornment={false}
                placeholder={intl.formatMessage({
                  id: 'containers.filtering.placeholder.filterBy',
                })}
                sx={{
                  mb: 1,
                  width: '100%',
                }}
                slotProps={{
                  input: {
                    sx: {
                      fontSize: ({ typography }) => typography.body2.fontSize,
                    },
                  },
                }}
              />
              <Stack sx={{ maxHeight: 500, overflowY: 'auto' }}>
                {filteredSettings.map(filter => {
                  const valuesAreLoading = !!filter.optionsAreLoading;

                  return (
                    <MenuItem
                      key={filter.filterKey}
                      disabled={valuesAreLoading}
                      onClick={() => setFilterCandidate(filter)}
                      sx={{ px: 2, py: 0.75 }}
                      data-cy={`add-filter-${filter.filterKey}`}
                    >
                      <Stack flexDirection="row" gap={0.5} alignItems="center">
                        {valuesAreLoading && <CircularProgress size={16} />}
                        <Typography variant="body2">{filter.name}</Typography>
                      </Stack>
                    </MenuItem>
                  );
                })}
              </Stack>
            </Stack>
          )}
        </Stack>
      </Popover>
      <Button
        variant="text"
        startIcon={
          <SvgIcon fontSize="large">
            <IconCirclePlus />
          </SvgIcon>
        }
        endIcon={isLoading ? <CircularProgress size={16} /> : null}
        color="secondary"
        sx={{ textWrap: 'nowrap' }}
        onClick={e => setAnchorEl(e.currentTarget)}
        data-cy="add-filter-button"
        disabled={isLoading || filterSettings.length === 0}
      >
        {intl.formatMessage({ id: 'containers.filtering.buttons.add' })}
      </Button>
    </>
  );
};
